/**
 * API client.
 *
 * Both URLs come from build-time env vars and are never hardcoded — the frontend and API deploy
 * from separate pipelines to separate origins, so the base URL genuinely differs between preview
 * and production.
 */

const rawBase = import.meta.env.VITE_API_BASE_URL;

if (!rawBase) {
  throw new Error(
    'VITE_API_BASE_URL is not set. Copy web/.env.example to web/.env.local for local development, ' +
      'or set it in the Cloudflare Pages project for deployed builds.',
  );
}

export const API_BASE_URL = rawBase.replace(/\/$/, '');

export const HUB_URL =
  import.meta.env.VITE_HUB_URL ?? `${API_BASE_URL}/hubs/conversation`;

/**
 * Events live in api/events.ts, alongside the wall-clock contract their times depend on. Only the
 * message cursor path stays here.
 */
export interface Message {
  id: string;
  seq: number;
  authorPersonId: string;
  authorDisplayName: string;
  authorParticipationMode: 'Active' | 'Monitor';
  /** Null when removed — the tombstone keeps author and timestamp. */
  body: string | null;
  postedAt: string;
  editedAt: string | null;
  isRemoved: boolean;
  removalBasis: string | null;
  isEdited: boolean;
  /**
   * What this reader may do with this message — `ModerationPolicy`'s answers, per row (TROOP-12).
   *
   * **Per row, and never re-derived here.** The action sheet gates its controls on these, so a
   * value the client worked out for itself would be a second definition of §5's rules, and a
   * constant would fail *silent* — taking the control away from the person who does have the right,
   * with nothing anywhere to diagnose (rule 14, TROOP-73). They genuinely vary down one page: an
   * author may take their own message down and not the one above it.
   */
  canRemove: boolean;
  canRestore: boolean;
}

export interface MessagePage {
  messages: Message[];
  nextCursor: number | null;
  hasMore: boolean;
}

export interface PostMessageResponse {
  id: string;
  seq: number;
  postedAt: string;
  /** True when an identical clientMessageId had already been accepted. */
  duplicate: boolean;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    // Session cookies must ride along; app and API share the registrable domain, so a
    // SameSite=Lax cookie works without any SameSite=None workaround.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    throw new ApiError(response.status, `${init?.method ?? 'GET'} ${path} failed`);
  }

  return (await response.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  /**
   * The reconcile path. Called on first paint, on every reconnect, and whenever the hub nudges —
   * they are the same call because the cursor is gapless, which is what lets the client be correct
   * when SignalR never connects at all.
   */
  getMessages: (
    unitId: string,
    conversationId: string,
    after?: number,
    limit = 50,
    // I3 oversight -- TROOP-14. Set only by a unit admin who has been told, in as many words, that
    // the read is recorded and shown to everybody in the thread. Sent on every page because every
    // page is a real read; the thread groups them per reader when it discloses them.
    oversight = false,
  ) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after !== undefined) {
      params.set('after', String(after));
    }
    if (oversight) {
      params.set('oversight', 'true');
    }
    return request<MessagePage>(
      `/units/${unitId}/conversations/${conversationId}/messages?${params}`,
    );
  },

  /**
   * No author field. The author is the authenticated session — sending one would
   * be ignored by the API, and accepting one would have let anyone post as anyone.
   */
  postMessage: (unitId: string, conversationId: string, body: string, clientMessageId: string) =>
    request<PostMessageResponse>(`/units/${unitId}/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, clientMessageId }),
    }),
};
